import React, { useCallback, useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { flightIconPath, flightClassIconPath, flightClassName, digits } from '@this/src/util';
import { Text } from '@this/components/shared/ui/data_displays/typography';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import type { Tab } from '@this/domain/select_store';
import type FlightSliceCandidate from '@this/domain/flight/flight_slice_candidate';
import {
  BoxContent,
  Left,
  Segment,
  Name,
  Image,
  BoxFooter
} from '../transport_box/transport_box_foreign.template';
import {
  BoxPriceArea,
  BoxPrice,
  BoxSmall,
  BoxButtonArea,
  BoxButton
} from '../transport_box/right/right_in_outline.template';
import { BoxButtonText, Yen } from '../shared/box_parts';
import { RightButton, RightBody, RightPrice, RightPeopleNum } from '../list_area/list_area.template';

export interface FlightBoxProps {
  // Outline/List 共通
  flight: FlightSliceCandidate | undefined;
  selected: boolean;
  inList: boolean;
  isDomestic: boolean;

  // Outline用
  handleClickTab?: (tab: Tab) => void;
  tab: number;
  flightIndex: number;

  // List用
  handleSelect?: (f: FlightSliceCandidate) => void;

  // ArrangementRequestForm
  isRequestForm?: boolean;
}

const FlightBox: React.FC<FlightBoxProps> = ({
  flight,
  selected,
  inList,
  isDomestic,
  handleClickTab,
  tab,
  flightIndex,
  handleSelect,
  isRequestForm
}) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const handleChange = useCallback(() => {
    if (inList) {
      if (handleSelect && flight) handleSelect(flight);
    } else if (handleClickTab && tab !== undefined) {
      handleClickTab(selected ? 'none' : tab);
    }
  }, [inList, handleSelect, flight, handleClickTab, tab, selected]);

  const flightSlice = flight?.cheapestFlight()?.itineraries?.[flightIndex];

  return (
    <div style={styles.flightBox}>
      {flight ? (
        <>
          <BoxContent>
            <Left>
              {flightSlice?.segments?.map((s, i) => (
                <Segment key={`segment-${s.from?.name}-${s.to?.name}-${i}`}>
                  <div className="flex">
                    <Name data-wovn-ignore>
                      {s.carrier_name && `${s.carrier_name} `}
                      {s.name}
                    </Name>
                    <Image src={flightIconPath(s.name)} />
                  </div>
                  <div className="select-transport-box__left__icon-body">
                    <img src="/images/departure.png" width="28" height="17" alt="" />
                    <div data-wovn-ignore className="select-transport-box__left__icon-body__text">
                      {`${s.from.date} ${s.from.time} ${s.from.name}`}
                    </div>
                  </div>
                  <div className="select-transport-box__left__icon-body">
                    <img src="/images/arrival.png" width="28" height="17" alt="" />
                    <div data-wovn-ignore className="select-transport-box__left__icon-body__text">
                      {`${s.to.date} ${s.to.time} ${s.to.name}`}
                    </div>
                  </div>
                  <div className="select-transport-box__left__icon-body">
                    <img
                      src={flightClassIconPath(s.cabin)}
                      title={flightClassName(s.cabin)}
                      width="28"
                      height="17"
                      alt="flight class icon"
                    />
                    <div data-wovn-ignore className="select-transport-box__left__icon-body__text">
                      {flightClassName(s.cabin)}
                    </div>
                  </div>
                </Segment>
              ))}
            </Left>
            {inList ? (
              <div className="select-transport-box__right-wrapper flight">
                {flightSlice?.ticketing_enable ? (
                  <RightButton
                    isDomestic={isDomestic}
                    selected={selected || flight.hovered}
                    onClick={handleChange}
                    onTouchStart={flight.handleTouched}
                    onMouseOver={() => flight.setHovered(true)}
                    onMouseLeave={() => flight.setHovered(false)}
                  >
                    {flight.hovered && !selected ? (
                      <div className="select-transport-box__right__body__select">選択する</div>
                    ) : (
                      <RightBody>
                        {tab === 0 ? (
                          <div>
                            <RightPeopleNum>1名</RightPeopleNum>
                            <RightPrice>
                              <span data-wovn-ignore>{digits(flight.pricePerPerson())}</span>
                              <Yen>円</Yen>
                            </RightPrice>
                          </div>
                        ) : (
                          <div>
                            <RightPeopleNum>1名</RightPeopleNum>
                            <RightPrice>+</RightPrice>
                            <RightPrice>
                              <span data-wovn-ignore>{digits(flight.priceDiffPerPerson())}</span>
                              <Yen>円</Yen>
                            </RightPrice>
                          </div>
                        )}
                      </RightBody>
                    )}
                  </RightButton>
                ) : (
                  <div className="select-transport-box__right flight disabled">
                    <RightBody>期限内に発券できません</RightBody>
                  </div>
                )}
                <div className="select-transport-box__right-deadline">
                  {(flightSlice?.rawTicketingExpiredAt || flightSlice?.timeToLive) && (
                    <div
                      title="発券期限は航空会社により予告なく変更される場合があります"
                      style={{ ...styles.deadlineWrap, cursor: 'pointer' }}
                    >
                      発券期限：
                      <Text color="danger" level="caption" bold>
                        {flightSlice?.rawTicketingExpiredAt
                          ? flightSlice?.rawTicketingExpiredAt?.format('YYYY/MM/DD HH:mm')
                          : `ご依頼から${flightSlice?.timeToLive}時間`}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={styles.flightBoxWrap}>
                <div className="flex">
                  <BoxPriceArea>
                    <div>
                      {flightIndex === 0 ? (
                        <BoxPrice>
                          <span data-wovn-ignore>{digits(flight.pricePerPerson())}</span>
                          <Yen>円</Yen>
                        </BoxPrice>
                      ) : (
                        [
                          <BoxPrice key={0}>+</BoxPrice>,
                          <BoxPrice key={1}>
                            <span data-wovn-ignore>{digits(flight.priceDiffPerPerson())}</span>
                            <Yen>円</Yen>
                          </BoxPrice>
                        ]
                      )}
                      <BoxSmall>/1名</BoxSmall>
                    </div>
                  </BoxPriceArea>
                  {!isRequestForm && (
                    <BoxButtonArea>
                      <BoxButton selected={selected} onClick={handleChange}>
                        <BoxButtonText>
                          {selected ? ['変更を', <br key={0} />, 'キャンセル'] : '変更 >'}
                        </BoxButtonText>
                      </BoxButton>
                    </BoxButtonArea>
                  )}
                </div>
                <div className="select-transport-box__right-deadline">
                  {(flightSlice?.rawTicketingExpiredAt || flightSlice?.timeToLive) && (
                    <div
                      title="発券期限は航空会社により予告なく変更される場合があります"
                      style={{ ...styles.deadlineWrap, cursor: 'pointer' }}
                    >
                      発券期限：
                      <Text color="danger" level="caption" bold>
                        {flightSlice?.rawTicketingExpiredAt ? (
                          <span data-wovn-ignore>
                            {flightSlice?.rawTicketingExpiredAt?.format('YYYY/MM/DD HH:mm')}
                          </span>
                        ) : (
                          <>
                            <span>ご依頼から</span>
                            <span data-wovn-ignore>{flightSlice?.timeToLive}</span>
                            <span>時間</span>
                          </>
                        )}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            )}
          </BoxContent>
          <BoxFooter selected={selected}>
            <div>
              <span>所要時間：</span>
              <span data-wovn-ignore>{flightSlice?.duration}</span>
            </div>
            <span>表記は現地時刻です。</span>
            {flight.cheapestFlightMinirulePerPerson() && (
              <span data-wovn-ignore>{`(${flight.cheapestFlightMinirulePerPerson()})`}</span>
            )}
          </BoxFooter>
        </>
      ) : (
        <Loading />
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  flightBox: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    border: `1px solid ${theme.borderColor}`,
    borderRadius: 4,
    backgroundColor: 'white',
    padding: 0,
    margin: 0,
    cursor: 'pointer'
  },
  flightBoxWrap: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    justifyContent: 'center' as const,
    padding: '10px 0'
  },
  deadlineWrap: {
    display: 'flex' as const,
    alignItems: 'center' as const,
    marginTop: 8,
    fontSize: 10,
    color: theme.grayTextColor
  }
});

export default FlightBox;
